import React from 'react';
import get from 'lodash.get';
import clsx from 'clsx';
import { Link } from 'acq-components/lib/base-components/Link/Link';
import { Button } from 'acq-components/lib/base-components/Button/Button';
import { Sanitizer } from 'acq-components/lib/base-components/Sanitizer/Sanitizer';
import { createDtmAndTrackActionHandler } from 'src/universal/utils/analyticsTracking';
import removeTagsAndTheirContent from 'service_modules/utils/text-remove-tags';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import { Image } from 'acq-components/lib/base-components/Image/Image';
import compareCardLocalstorage from 'service_modules/persistence/compare-card/compare-card-localstorage';
import { formatLinksWithEep } from './index';
import { decideCardArtUrl } from 'src/universal/utils/cardColorChoices';
import { sendDTMClickEvent } from 'service_modules/models/dtm-tagging';

/* eslint max-params: 0 */

export const buildXSellCardList = (
  cardList,
  eep,
  pznOffersEepByPmc,
  styles,
  parentCardName,
  keepWordsInCardFeeList,
  colorInfoList
) => {
  const buildDisclaimer = text => {
    return text ? `<div class="${styles.annualFeeDisclaimer}">${text}</div>` : '';
  };

  return (cardList || []).map(card => {
    const links = parseArrayToObject(card.links, 'key');
    const formattedLinks = formatLinksWithEep({
      links,
      eep: pznOffersEepByPmc[card.pmcCode] || eep,
      appendEep: true
    });
    const cardArtHref = get(formattedLinks, 'cardArt.url');
    const cardTitleHref = get(formattedLinks, 'cardTitle.url');
    const annualFeeRegex = /\$[0-9]*/g;
    let annualFeeNumberOnly = `${card.annualFee.match(
      annualFeeRegex
    )}<sup>&#164;</sup>`;
    let cardTitle = get(card, 'cardHeadlines.title');
    if (!cardTitle) {
      cardTitle = card.cardHeadline || '';
    }
    if (
      (Array.isArray(keepWordsInCardFeeList) ? keepWordsInCardFeeList : []).indexOf(
        card.shortName
      ) > -1
    ) {
      annualFeeNumberOnly = card.annualFee;
    }
    const ariaCardTitle = removeTagsAndTheirContent(card.cardHeadline);
    const colorInfo = colorInfoList.find(c => c.shortName === card.shortName);
    const cardArtUrl = decideCardArtUrl(
      get(card, 'appAssets.cardArt'),
      colorInfo,
      card.shortName
    );
    const disclaimer = colorInfo
      ? buildDisclaimer(colorInfo.rubiksContent?.annualFeeDisclaimer)
      : '';

    const imageDtm = card?.appAssetsDtm;

    return {
      productType: 'card',
      ImageComponent: Link,
      imageComponentProps: {
        Component: Link,
        className: styles.productImageContainer,
        href: cardArtHref,
        children: cardArtUrl && (
          <Image
            className={styles.productImage}
            src={cardArtUrl}
            alt={removeTagsAndTheirContent(cardTitle)}
            onClick={() => {
              sendDTMClickEvent(imageDtm);
            }}
          />
        )
      },
      TitleComponent: Link,
      titleComponentProps: {
        Component: Link,
        href: cardTitleHref,
        className: styles.productTitle,
        children: <Sanitizer Component="h3" safelySetInnerHTML={cardTitle} />,
        onClick: () => sendDTMClickEvent(card?.titleDtm)
      },
      productFootnotes: [
        {
          Component: Sanitizer,
          componentProps: {
            Component: 'div',
            safelySetInnerHTML: `<span>Annual Fee:</span> ${annualFeeNumberOnly} ${disclaimer} <div class="${
              styles.cardHeader
            }">${get(card, 'cardFeatures.header')}</div>`,
            className: clsx(styles.productDescription, {
              [styles.needFeeDisclaimer]: !!disclaimer
            })
          }
        }
      ],
      ariaCardTitle,
      productLinks: [
        formattedLinks.learnMore,
        formattedLinks.compare,
        formattedLinks.offerTerms,
        formattedLinks.ratesFees
      ]
        .filter(link => !!link)
        .map(link => {
          const ariaLabelMap = {
            learnMore: 'about ',
            compare: '',
            offerTerms: 'for ',
            ratesFees: 'for '
          };
          return {
            key: link.key,
            children: link.text,
            link: link.key !== 'compare' ? link.url : '',
            styles: styles,
            component: link.key === 'compare' ? Button : Link,
            hoverText: link.title,
            'aria-label': `${removeTagsAndTheirContent(link.text)} ${
              ariaLabelMap[link.key]
            }${removeTagsAndTheirContent(card.cardHeadline)}`,
            target: ['offerTerms', 'ratesFees'].includes(link.key) ? '_blank' : '',
            onClick: () => {
              if (link.key === 'compare') {
                const cardsToCompare = [parentCardName, card.shortName];
                compareCardLocalstorage.selected.set(cardsToCompare);
                compareCardLocalstorage.activity.set(true);
                setTimeout(() => {
                  window.location.href = link.url;
                }, 5);
              }
              createDtmAndTrackActionHandler({
                dtmObj: link.dtm
              })();
            }
          };
        })
    };
  });
};

export const hasPznSuppliedXSellData = xSellList => {
  if (!Array.isArray(xSellList) || xSellList.length <= 0) {
    return false;
  }

  // we use "for" loop so we can short-circuit it.
  for (let i = 0; i < xSellList.length; i++) {
    const cardArt = get(xSellList[i], 'appAssets.cardArt');
    if (typeof cardArt !== 'string' || cardArt.length <= 0) {
      return false;
    }
  }
  return true;
};
